import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, AttributionControl } from "react-leaflet";
import IsLoading from "./IsLoading";
import HasError from "./HasError";
import { useSelector } from "react-redux";
import MyCustomMarker from "./MyCustomMarker";
import useWindowDimensions from "./useWindowDimensions";

const StationsMap = ({ AQIType, setAQIType }) => {
  const { screenWidth, screenHeight } = useWindowDimensions();

  // Selectors for stations data
  const stationsHourly = useSelector((state) => state.stationsHourly);
  const stationsDaily = useSelector((state) => state.stationsDaily);
  const stationsTwelve = useSelector((state) => state.stationsTwelve);
  const stationsTwentyFour = useSelector((state) => state.stationsTwentyFour);
  const stations = useSelector((state) => state.stations);

  const information = useSelector((state) => state.information);

  const stationsHourlyData = stationsHourly.stationsData;
  const stationsHourlyLoading = stationsHourly.stationsLoading;
  const stationsHourlyError = stationsHourly.stationsError;

  const stationsDailyData = stationsDaily.stationsData;
  const stationsDailyLoading = stationsDaily.stationsLoading;
  const stationsDailyError = stationsDaily.stationsError;

  const stationsTwelveData = stationsTwelve.stationsData;
  const stationsTwelveLoading = stationsTwelve.stationsLoading;
  const stationsTwelveError = stationsTwelve.stationsError;

  const stationsTwentyFourData = stationsTwentyFour.stationsData;
  const stationsTwentyFourLoading = stationsTwentyFour.stationsLoading;
  const stationsTwentyFourError = stationsTwentyFour.stationsError;

  const newSataData = stations;

  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;

  // State for loading and error
  const [stationsLoading, setStationsLoading] = useState();
  const [stationsError, setStationsError] = useState();
  const [theStations, setTheStations] = useState([]);
  const [mapZoom, setMapZoom] = useState(5.6);
  const [mapHeight, setMapHeight] = useState("90vh");

  // Function to get device information based on device type and ID
  const GetInformationOfTheDevice = useCallback(
    ({ deviceType, deviceId }) => {
      const theSpecificStation =
        informationData.find(
          (x) =>
            x.device === String(deviceType) && x.device_id === Number(deviceId)
        ) || {};

      return {
        device_id: theSpecificStation.device_id,
        device: theSpecificStation.device,
        is_kit: theSpecificStation.is_kit,
        is_test: theSpecificStation.is_test,
        is_opm: theSpecificStation.is_opm,
        user: theSpecificStation.user,
        province: theSpecificStation.province,
        city: theSpecificStation.city,
        stationName: theSpecificStation.stationName,
        longitude: theSpecificStation.longitude,
        latitude: theSpecificStation.latitude,
        slug: theSpecificStation.slug,
      };
    },
    [informationData]
  );

  // Reusable function to modify station data
  const modifyStationData = useCallback(
    (stationsData, deviceType) => {
      if (stationsData[deviceType] && Array.isArray(stationsData[deviceType])) {
        return stationsData[deviceType].map((element) => {
          const deviceInfo = GetInformationOfTheDevice({
            deviceType: deviceType,
            deviceId: element.device_id,
          });
          return { ...element, ...deviceInfo };
        });
      }
      return [];
    },
    [GetInformationOfTheDevice]
  );

  // Memoize modified station data
  const modifiedHourlyStationsList = useMemo(
    () => [
      ...modifyStationData(stationsHourlyData, "SAPA"),
      ...modifyStationData(stationsHourlyData, "SATA"),
      ...modifyStationData(stationsHourlyData, "ESPS"),
      ...modifyStationData(stationsHourlyData, "OSPM"),
    ],
    [stationsHourlyData, modifyStationData]
  );

  const modifiedTwelveStationsList = useMemo(
    () => [
      ...modifyStationData(stationsTwelveData, "SAPA"),
      ...modifyStationData(stationsTwelveData, "SATA"),
      ...modifyStationData(stationsTwelveData, "ESPS"),
      ...modifyStationData(stationsTwelveData, "OSPM"),
    ],
    [stationsTwelveData, modifyStationData]
  );

  const modifiedDailyStationsList = useMemo(
    () => [
      ...modifyStationData(stationsDailyData, "SAPA"),
      ...modifyStationData(stationsDailyData, "SATA"),
      ...modifyStationData(stationsDailyData, "ESPS"),
      ...modifyStationData(stationsDailyData, "OSPM"),
    ],
    [stationsDailyData, modifyStationData]
  );

  const modifiedTwentyFourStationsList = useMemo(
    () => [
      ...modifyStationData(stationsTwentyFourData, "SAPA"),
      ...modifyStationData(stationsTwentyFourData, "SATA"),
      ...modifyStationData(stationsTwentyFourData, "ESPS"),
      ...modifyStationData(stationsTwentyFourData, "OSPM"),
    ],
    [stationsTwentyFourData, modifyStationData]
  );

  // Combine station data lists and loading/error states by AQI type
  const stationLists = useMemo(
    () => ({
      hourly: {
        list: modifiedHourlyStationsList,
        loading: stationsHourlyLoading,
        error: stationsHourlyError,
      },
      twelve: {
        list: modifiedTwelveStationsList,
        loading: stationsTwelveLoading,
        error: stationsTwelveError,
      },
      daily: {
        list: modifiedDailyStationsList,
        loading: stationsDailyLoading,
        error: stationsDailyError,
      },
      twentyFour: {
        list: modifiedTwentyFourStationsList,
        loading: stationsTwentyFourLoading,
        error: stationsTwentyFourError,
      },
    }),
    [
      modifiedHourlyStationsList,
      modifiedTwelveStationsList,
      modifiedDailyStationsList,
      modifiedTwentyFourStationsList,
      stationsHourlyLoading,
      stationsHourlyError,
      stationsTwelveLoading,
      stationsTwelveError,
      stationsDailyLoading,
      stationsDailyError,
      stationsTwentyFourLoading,
      stationsTwentyFourError,
    ]
  );

  // Fetch data based on AQI type
  useEffect(() => {
    const { list, loading, error } = stationLists[AQIType];
    setTheStations(list);
    setStationsLoading(loading);
    setStationsError(error);
  }, [AQIType, stationLists]);

  // Adjust map settings based on screen width
  // Memoize map settings calculation
  useEffect(() => {
    setMapZoom(screenWidth < 600 ? 2 : 5.6);
    setMapHeight(screenWidth < 600 ? "70vh" : "90vh");
  }, [screenWidth]);

  if (stationsLoading || informationLoading) {
    return <IsLoading />;
  } else if (stationsError || informationError) {
    return <HasError />;
  } else if (theStations.length > 0) {
    return (
      <div className="container my-5">
        <div
          className="bg-secondary rounded-1 p-2"
          style={{ width: "90%", margin: "auto" }}
        >
          <div className="col-md-4">
            <select
              name="average"
              id="average"
              className="form-select w-100 form-select-lg"
              aria-label="Default select example"
              onChange={(e) => setAQIType(e.target.value)}
              value={AQIType}
            >
              <option value="daily">شاخص روزانه</option>
              <option value="twentyFour">شاخص آنلاین</option>
              <option value="twelve">شاخص هم اکنون</option>
              <option value="hourly">شاخص ساعتی</option>
            </select>
          </div>
        </div>
        <MapContainer
          id="payeshMap"
          center={[32.739, 53.688]}
          zoom={mapZoom}
          zoomSnap={0.1}
          style={{
            width: "90%",
            height: mapHeight,
            margin: "auto",
          }}
          //remove Ukraine flag
          attributionControl={false}
        >
          {theStations.map((station) => {
            // Skip rendering the marker if the station meets the specified condition
            if (station.device_id === 101 && station.device === "SATA") {
              return null; // Use 'null' instead of an empty string for better readability
            } else {
              return (
                <MyCustomMarker
                  key={station.slug}
                  theKey={station.slug}
                  information={station}
                />
              );
            }
          })}
          {theStations.map((station) => {
            // Skip rendering the marker if the station meets the specified condition
            if (station.device === "SATA2") {
              return null; // Use 'null' instead of an empty string for better readability
            } else {
              return (
                <MyCustomMarker
                  key={station.slug}
                  theKey={station.slug}
                  information={station}
                />
              );
            }
          })}
          <TileLayer
            attribution="ایستگاه های فعال کنترل کیفیت هوا"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AttributionControl position="bottomright" prefix={false} />
          {/*remove ukraine flag */}
        </MapContainer>
      </div>
    );
  }
};

export default StationsMap;
